import React, { useState, useEffect } from "react";
import { Download } from "lucide-react";
import BlogPost from "./BlogPost";
import EmailCaptureModal from "./EmailCaptureModal";
import { supabase } from "../lib/supabase";

interface BlogPostWithDownloadProps {
  slug: string;
  previewMode?: boolean;
}

export default function BlogPostWithDownload({
  slug,
  previewMode = false,
}: BlogPostWithDownloadProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch the PDF URL from the uploads table
    const fetchPdfUrl = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("uploads")
          .select("url")
          .eq("name", "Nonprofit_AI_Ethics_Checklist_v2.pdf")
          .eq("deleted", false)
          .single();

        if (error) {
          console.error("Error fetching PDF URL:", error);
          // Fallback to the static file if database fetch fails
          setPdfUrl("/resources/Nonprofit_AI_Ethics_Checklist_v2.pdf");
        } else if (data) {
          console.log("Found PDF in uploads table:", data);
          setPdfUrl(data.url);
        } else {
          console.warn("PDF not found in uploads table, using fallback");
          setPdfUrl("/resources/Nonprofit_AI_Ethics_Checklist_v2.pdf");
        }
      } catch (err) {
        console.error("Error in fetchPdfUrl:", err);
        setPdfUrl("/resources/Nonprofit_AI_Ethics_Checklist_v2.pdf");
      } finally {
        setLoading(false);
      }
    };

    fetchPdfUrl();
  }, []);

  useEffect(() => {
    // Function to replace download placeholders with actual download buttons
    const replaceDownloadPlaceholders = () => {
      // Find all download placeholders
      const downloadPlaceholderElements = document.querySelectorAll(
        "#nonprofit-ai-checklist-download",
      );

      if (downloadPlaceholderElements.length > 0) {
        console.log(
          `Found ${downloadPlaceholderElements.length} download placeholders`,
        );
      }

      // For each placeholder, replace with the download button
      downloadPlaceholderElements.forEach((placeholder) => {
        if (!placeholder.querySelector(".download-button")) {
          // Create the button element
          const button = document.createElement("button");
          button.className =
            "download-button inline-flex items-center px-4 py-2 bg-[#F98B3D] text-white rounded-md hover:bg-[#e07a2c] transition-colors duration-200";
          button.innerHTML = `
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-5 w-5 mr-2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download the Non-Profit AI Ethics Checklist (PDF)
          `;

          // Add click event listener
          button.addEventListener("click", () => {
            // Show email capture modal
            setIsModalOpen(true);
          });

          // Replace the placeholder with the button
          placeholder.parentNode?.replaceChild(button, placeholder);
          console.log("Replaced download placeholder with button");
        }
      });
    };

    // Set up a mutation observer to watch for changes in the DOM
    const observer = new MutationObserver((mutations) => {
      replaceDownloadPlaceholders();
    });

    // Start observing the document with the configured parameters
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Initial check after a delay to ensure the DOM is loaded
    setTimeout(replaceDownloadPlaceholders, 500);

    // Check periodically as a fallback
    const interval = setInterval(replaceDownloadPlaceholders, 1000);

    // Clean up
    return () => {
      observer.disconnect();
      clearInterval(interval);
    };
  }, [setIsModalOpen]);

  return (
    <>
      <BlogPost slug={slug} previewMode={previewMode} />

      {pdfUrl && (
        <EmailCaptureModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          downloadUrl={pdfUrl}
          source="Download the Non-Profit AI Ethics Checklist"
          title="Download Your Free Resource"
          description="Please enter your name and email to receive the Non-Profit AI Ethics Checklist. This comprehensive guide will help your non-profit organization implement AI ethically and effectively in Omaha, Nebraska, and throughout the Midwest region."
        />
      )}
    </>
  );
}
